import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Wrench, History, Undo2 } from "lucide-react";
import { toast } from "sonner";

export function SchemaRepairs() {
  const [items, setItems] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/seo/schema-manager/repairs").then((r) => { setItems(r.data.items); setPicked([]); }), []);
  useEffect(() => { load(); }, [load]);
  const apply = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/seo/schema-manager/repairs/apply", { ids: picked }); toast.success(`${r.data.applied} repair(s) applied`); load(); }
    catch { toast.error("Could not apply"); } finally { setBusy(false); }
  };
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <section data-testid="schema-repairs">
      <h3 className="font-bold text-steel flex items-center gap-2 mb-1"><Wrench size={18} /> Safe repair suggestions</h3>
      <p className="text-xs text-muted-foreground mb-3">Only reversible, data-backed fixes. Nothing is applied until you approve it.</p>
      {!items ? <Loader2 size={16} className="animate-spin" /> : items.length === 0
        ? <p className="text-sm text-emerald-600" data-testid="schema-repairs-empty">No safe repairs needed.</p>
        : (
          <>
            <div className="flex gap-2 mb-2">
              <Button size="sm" variant="outline" onClick={() => setPicked(picked.length === items.length ? [] : items.map((i) => i.id))} data-testid="schema-repairs-select-all">
                {picked.length === items.length ? "Clear selection" : `Select all (${items.length})`}
              </Button>
              <Button size="sm" className="bg-accent text-steel hover:bg-amber-600 font-semibold" disabled={!picked.length || busy} onClick={apply} data-testid="schema-repairs-apply">
                {busy && <Loader2 size={14} className="mr-1 animate-spin" />} Approve {picked.length} repair(s)
              </Button>
            </div>
            <div className="rounded-xl border bg-white divide-y max-h-72 overflow-y-auto">
              {items.map((i) => (
                <label key={i.id} className="flex items-start gap-3 p-3 text-sm cursor-pointer">
                  <Checkbox checked={picked.includes(i.id)} onCheckedChange={() => toggle(i.id)} data-testid={`schema-repair-${i.id}`} />
                  <div><div className="font-semibold text-steel line-clamp-1">{i.label}</div><div className="text-xs text-muted-foreground">{i.problem} → {i.fix}</div></div>
                </label>
              ))}
            </div>
          </>
        )}
    </section>
  );
}

export function SchemaHistory() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/seo/schema-manager/history").then((r) => setItems(r.data.items)), []);
  useEffect(() => { load(); }, [load]);
  const rollback = async (id) => {
    try { await api.post(`/admin/seo/schema-manager/history/${id}/rollback`); toast.success("Rolled back"); load(); }
    catch { toast.error("Rollback failed"); }
  };
  const short = (v) => { const s = typeof v === "string" ? v : JSON.stringify(v); return !s ? "(empty)" : s.length > 60 ? s.slice(0, 60) + "…" : s; };
  return (
    <section data-testid="schema-history">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-steel flex items-center gap-2"><History size={18} /> Schema audit history</h3>
        <Button size="sm" variant="outline" onClick={load} data-testid="schema-history-refresh">Refresh</Button>
      </div>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No schema changes yet.</p> : (
        <div className="rounded-xl border bg-white divide-y max-h-72 overflow-y-auto">
          {items.map((h) => (
            <div key={h.id} className="p-3 text-xs flex items-center justify-between gap-3" data-testid={`schema-history-${h.id}`}>
              <div>
                <div className="font-semibold text-steel">{h.etype} · {h.key} · {h.field} <span className="text-muted-foreground font-normal">({h.source} by {h.editor}, {new Date(h.at).toLocaleString()})</span></div>
                <div className="text-muted-foreground font-mono">{short(h.old)} → {short(h.new)}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => rollback(h.id)} data-testid={`schema-rollback-${h.id}`}><Undo2 size={13} className="mr-1" /> Rollback</Button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
